import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { IDENTIFIED_BY_LABEL } from '@aeon-karaoke/cloud-convex/model/identify';
import { TRACK_STATE_LABEL, trackState, type TrackState } from '@aeon-karaoke/cloud-convex/model/trackState';
import { Button, EmptyState, Input, LoadingState, MonoLabel, Star, StatusDot, Tabs, buttonClassName } from '@/component';
import { useFavourites, useMySongMatches, useMyTracks, useMyTracksSummary, type SongMatch, type TrackView } from '@/hook';
import { cn } from '@/lib/cn';
import { formatDuration } from '@/lib/time';
import { ManageTrackModal } from './ManageTrackModal';

type Filter = 'all' | TrackState;

const STATE_TEXT = {
  ready: 'text-up',
  preparing: 'text-acc',
  attention: 'text-down',
} satisfies Record<TrackState, string>;

/** "Linger", "Linger and Dreams", "Linger and 2 more". */
function nameFailed(titles: string[]): string {
  const [first, second, ...rest] = titles;
  if (!first) return '';
  if (!second) return first;
  if (rest.length === 0) return `${first} and ${second}`;
  return `${first} and ${rest.length + 1} more`;
}

/** "Matched: The Cranberries · Linger", for the row's indicator. */
function matchTitle(match: SongMatch | undefined): string | undefined {
  return match ? `Matched: ${[match.artist, match.title].filter(Boolean).join(' · ')}` : undefined;
}

/** /library/mine: the management surface for the caller's creations. Preparing
 * and failed tracks live here, not in the catalogue. */
export function MyTracksPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');
  const [managing, setManaging] = useState<TrackView | null>(null);
  const { favourites, toggle: toggleFavourite } = useFavourites();
  const matches = useMySongMatches();
  const summary = useMyTracksSummary();
  const { results, status, loadMore } = useMyTracks(filter === 'all' ? undefined : filter);

  const query = search.trim().toLowerCase();
  const rows = query
    ? results.filter((t) => t.title.toLowerCase().includes(query) || t.artist?.toLowerCase().includes(query))
    : results;

  const tabs = [
    { value: 'all', label: 'All', count: summary?.all },
    { value: 'ready', label: 'Ready', count: summary?.ready },
    { value: 'preparing', label: 'Preparing', count: summary?.preparing },
    { value: 'attention', label: 'Needs attention', count: summary?.attention },
  ] as const;

  return (
    <div className="pt-10">
      <Link to="/library" className="hover:text-ink">
        <MonoLabel tone="mute">← Library</MonoLabel>
      </Link>
      <div className="mt-[26px] flex flex-wrap items-end justify-between gap-7">
        <div>
          <MonoLabel tone="acc" size="md" className="block">
            Yours{summary && ` · ${summary.all} ${summary.all === 1 ? 'track' : 'tracks'}`}
          </MonoLabel>
          <h1 className="mt-3 text-[42px] font-semibold leading-[1.04] tracking-[-0.035em]">Tracks you made</h1>
        </div>
        <div className="flex flex-wrap gap-[10px]">
          <Input
            aria-label="Search your tracks"
            placeholder="Search your tracks"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-[42px] w-[230px] text-sm"
          />
          <Link to="/create" className={buttonClassName({ className: 'px-[18px]' })}>
            Create karaoke track
          </Link>
        </div>
      </div>

      {summary && summary.failed.length > 0 && (
        <div className="mt-8 flex flex-wrap items-center justify-between gap-5 rounded-lg border border-down bg-surface px-[22px] py-[18px]">
          <div className="min-w-[240px] flex-1">
            <MonoLabel tone="down" size="md" className="block">
              {summary.failed.length} {summary.failed.length === 1 ? 'needs' : 'need'} attention
            </MonoLabel>
            <p className="mt-2 text-[15px] leading-[1.5]">
              {nameFailed(summary.failed.map((f) => f.title))} could not be finished. Nothing was charged against your
              monthly count.
            </p>
          </div>
          <Link
            to="/create"
            search={{ track: summary.failed[0]?._id }}
            className={buttonClassName({ variant: 'outline', size: 'sm', className: 'px-4 py-[11px]' })}
          >
            Fix it
          </Link>
        </div>
      )}

      <Tabs tabs={tabs} value={filter} onChange={setFilter} className="mt-9" />

      {status === 'LoadingFirstPage' ? (
        <LoadingState />
      ) : rows.length === 0 ? (
        summary?.all === 0 ? (
          <EmptyState title="You have not made a track yet." body="Upload a song and we build the karaoke track." />
        ) : (
          <EmptyState title={query ? 'No tracks match.' : 'Nothing here.'} />
        )
      ) : (
        <div>
          {rows.map((track) => {
            const state = trackState(track.processingStatus);
            return (
              <div
                key={track._id}
                className="grid grid-cols-[24px_minmax(0,2.2fr)_minmax(0,1.3fr)_150px_100px_62px_88px_26px] items-center gap-4 border-b border-stripeA py-4"
              >
                <StatusDot tone={state} className="justify-self-center" />
                <Link
                  to="/tracks/$trackId"
                  params={{ trackId: track._id }}
                  className="min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em] text-ink hover:text-acc"
                >
                  {track.title}
                </Link>
                <div className="truncate text-sm text-mute">{track.artist}</div>
                <MonoLabel className={STATE_TEXT[state]}>{TRACK_STATE_LABEL[state]}</MonoLabel>
                <MonoLabel
                  tone={matches.get(track._id)?.identifiedBy === 'text' ? 'faint' : 'mute'}
                  title={matchTitle(matches.get(track._id))}
                  className="truncate"
                >
                  {(() => {
                    const by = matches.get(track._id)?.identifiedBy;
                    return by ? IDENTIFIED_BY_LABEL[by] : '';
                  })()}
                </MonoLabel>
                <div className="text-right font-mono text-xs tabular-nums text-mute">
                  {track.durationMs !== undefined ? formatDuration(track.durationMs) : ''}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setManaging(track)}
                  className={cn('rounded-[5px] px-[11px] py-[7px] text-xs')}
                >
                  Manage
                </Button>
                <Star on={favourites.has(track._id)} onToggle={() => toggleFavourite(track._id)} />
              </div>
            );
          })}
        </div>
      )}
      {status === 'CanLoadMore' && (
        <Button variant="outline" quiet onClick={() => loadMore(50)} className="mt-6">
          Load more
        </Button>
      )}

      <ManageTrackModal track={managing} onClose={() => setManaging(null)} />
    </div>
  );
}
